import { createApi } from '@reduxjs/toolkit/query/react';

import {
  BranchesEndpoints as Endpoints,
  type GetBranchInstructionsRequest,
  type GetBranchInstructionsResponse,
  type GetBranchRequest,
  type GetBranchResponse,
  type GetBranchesByOrganizationIdRequest,
  type GetBranchesByOrganizationIdResponse,
  type GetBranchesCountRequest,
  type GetBranchesCountResponse,
  type GetBranchesForSystemRequest,
  type GetBranchesForSystemResponse,
  type GetBranchesRequest,
  type GetBranchesResponse,
  type GetBranchPreBlockWarningRequest,
  type GetBranchPreBlockWarningResponse,
  type GetBranchWorkTimesOnDateRequest,
  type GetBranchWorkTimesOnDateResponse,
  type GetBranchWorkTimesOnPeriodRequest,
  type GetBranchWorkTimesOnPeriodResponse,
  type GetUserBranchListRequest,
  type GetUserBranchListResponse,
  type GetUserBranchesRequest,
  type GetUserBranchesResponse,
  type SearchBranchesRequest,
  type SearchBranchesResponse,
  type SetBranchInstructionsRequest,
  type SetBranchInstructionsResponse,
  type UpdateBranchRequest,
  type UpdateBranchResponse,
} from '@app/api/Branches/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { BRANCHES_CHUNK } from '@app/constants';

export const branchesApi2 = createApi({
  reducerPath: 'branchesApi2',
  baseQuery: createApiBaseQuery(BRANCHES_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.UpdateBranch]: mutation<
      UpdateBranchResponse,
      UpdateBranchRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateBranch,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranches]: query<GetBranchesResponse, GetBranchesRequest>({
      query: (params) => ({
        url: Endpoints.GetBranches,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchesForSystem]: query<
      GetBranchesForSystemResponse,
      GetBranchesForSystemRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchesForSystem,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranch]: query<GetBranchResponse, GetBranchRequest>({
      query: (params) => ({
        url: Endpoints.GetBranch,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchesCount]: query<
      GetBranchesCountResponse,
      GetBranchesCountRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchesCount,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetUserBranches]: query<
      GetUserBranchesResponse,
      GetUserBranchesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserBranches,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetUserBranchList]: query<
      GetUserBranchListResponse,
      GetUserBranchListRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserBranchList,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchBranches]: mutation<
      SearchBranchesResponse,
      SearchBranchesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchBranches,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranchWorkTimesOnDate]: query<
      GetBranchWorkTimesOnDateResponse,
      GetBranchWorkTimesOnDateRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchWorkTimesOnDate,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchWorkTimesOnPeriod]: query<
      GetBranchWorkTimesOnPeriodResponse,
      GetBranchWorkTimesOnPeriodRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchWorkTimesOnPeriod,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetBranchInstructions]: mutation<
      SetBranchInstructionsResponse,
      SetBranchInstructionsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetBranchInstructions,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranchInstructions]: query<
      GetBranchInstructionsResponse,
      GetBranchInstructionsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchInstructions,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchesByOrganizationId]: query<
      GetBranchesByOrganizationIdResponse,
      GetBranchesByOrganizationIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchesByOrganizationId,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchPreBlockWarning]: query<
      GetBranchPreBlockWarningResponse,
      GetBranchPreBlockWarningRequest
    >({
      // Вопреки swagger (где ответ описан как `string`), бэкенд заворачивает
      // текст в общий envelope: { Success, ErrorMessage, JsonData, ... }.
      // Поэтому обычный разбор customBaseQuery достаёт строку из `JsonData`.
      query: (params) => ({
        url: Endpoints.GetBranchPreBlockWarning,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useUpdateBranchMutation,
  useGetBranchesQuery,
  useLazyGetBranchesQuery,
  useGetBranchesForSystemQuery,
  useLazyGetBranchesForSystemQuery,
  useGetBranchQuery,
  useLazyGetBranchQuery,
  useGetBranchesCountQuery,
  useLazyGetBranchesCountQuery,
  useGetUserBranchesQuery,
  useLazyGetUserBranchesQuery,
  useGetUserBranchListQuery,
  useLazyGetUserBranchListQuery,
  useSearchBranchesMutation,
  useGetBranchWorkTimesOnDateQuery,
  useLazyGetBranchWorkTimesOnDateQuery,
  useGetBranchWorkTimesOnPeriodQuery,
  useLazyGetBranchWorkTimesOnPeriodQuery,
  useSetBranchInstructionsMutation,
  useGetBranchInstructionsQuery,
  useLazyGetBranchInstructionsQuery,
  useGetBranchesByOrganizationIdQuery,
  useLazyGetBranchesByOrganizationIdQuery,
  useGetBranchPreBlockWarningQuery,
  useLazyGetBranchPreBlockWarningQuery,
} = branchesApi2;
